import { apiService } from './api';

/** Mirrors the backend LeaveRequestStatus enum (serialised as an int). */
export enum LeaveRequestStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  Cancelled = 3,
}

export interface LeaveRequest {
  id: number;
  employeeId: number;
  employeeName: string;
  leaveType: string;
  /** DateOnly — "YYYY-MM-DD". */
  startDate: string;
  endDate: string;
  reason?: string;
  status: LeaveRequestStatus;
  reviewedByUserId?: number | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
}

export interface LeaveRequestFilter {
  pageNumber?: number;
  pageSize?: number;
  employeeId?: number;
  /** Status name as the API expects it, e.g. "Pending". */
  status?: string;
}

export interface CreateLeaveRequestDTO {
  employeeId: number;
  leaveType: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

export interface ReviewLeaveRequestDTO {
  approve: boolean;
  reviewNotes?: string;
}

/** Leave types the UI offers; the API stores the value as free text. */
export const LEAVE_TYPES = ['Annual', 'Casual', 'Sick', 'Unpaid'];

const leaveRequestService = {
  getAll: async (filter: LeaveRequestFilter = {}) => {
    const response = await apiService.get<LeaveRequest[]>('/hr/leave-requests', {
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 100,
      employeeId: filter.employeeId,
      status: filter.status,
    });
    return {
      items: response.data.data ?? [],
      pagination: response.data.pagination,
    };
  },

  getById: async (id: number) => {
    const response = await apiService.get<LeaveRequest>(`/hr/leave-requests/${id}`);
    return response.data.data;
  },

  create: async (dto: CreateLeaveRequestDTO) => {
    const response = await apiService.post<LeaveRequest>('/hr/leave-requests', dto);
    return response.data.data;
  },

  /** Approve or reject a pending request; needs LeaveRequests.Review. */
  review: async (id: number, dto: ReviewLeaveRequestDTO) => {
    const response = await apiService.post<LeaveRequest>(`/hr/leave-requests/${id}/review`, dto);
    return response.data.data;
  },

  cancel: (id: number) => apiService.post(`/hr/leave-requests/${id}/cancel`, {}),
};

export default leaveRequestService;
